import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyApprovalPayload } from '@edtech-ui/core/models/company-management/accept-reject-company-approval';
import { CompanyDetailsById } from '@edtech-ui/core/models/company-management/get-companies-by-id-page';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-company-approval-detail-page',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, FormsModule],
	templateUrl: './company-approval-detail-page.component.html',
	styleUrl: './company-approval-detail-page.component.scss'
})
export class CompanyApprovalDetailPageComponent implements OnInit, OnDestroy {

	companyDetails!: CompanyDetailsById;
	companyId = '';
	isLoading = false;
	isApprovalLoading = false;
	rejectionReason = ''
	showApproveModal = false;
	showRejectModal = false;
	showClarificationModal = false;
	clarificationReason = '';
	selectedCompany: any = null;
	showCompanyEditModal = false;
	showContactEditModal = false;
	isEditingCompany: boolean = false;
	isEditingContact: boolean = false;
	originalCompanyDetails: any;
	isSavingCompany = false;
	subscription = new Subscription();



	origin = '';
	pageParam = 1;



	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private companyService: CompanyService,
		private toastr: ToastrService
	) { }



	ngOnInit(): void {
		this.companyId = this.route.snapshot.paramMap.get('id') || '';
		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				this.origin = params['origin'] || '';
				this.pageParam = Number(params['page']) || 1;
			})
		);

		if (this.companyId) {
			this.getCompanyById();
		}
	}


	getCompanyById() {
		this.isLoading = true;
		this.subscription.add(
			this.companyService.getCompanyById(this.companyId).pipe(
				tap(response => {
					if (response.Success) {
						this.companyDetails = response.Data;
					} else {
						this.toastr.error(response.Message);
					}
				}), finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		);
	}


	goToCompany() {
		const queryParams = this.pageParam > 1 ? { page: this.pageParam } : {};
		if (this.origin === 'all-companies') {
			this.router.navigate(['admin-student-platform/company-management/all-companies'], { queryParams });
		} else {
			this.router.navigate(['admin-student-platform/company-management/company-approval'], { queryParams });
		}
	}


	postAcceptRejectCompanyApproval(action: 'approve' | 'reject' | 'block' | 'need_clarification') {

		if (!this.companyId) {
			return;
		}

		const payload: CompanyApprovalPayload = {
			action: action,
			notes:
				action === 'approve' ? 'Company approved successfully' : (action === 'need_clarification' ? 'Need Clarification' : 'Company actioned'),
			rejection_reason: action === 'reject' ? this.rejectionReason : null,
			clarification_reason: action === 'need_clarification' ? this.clarificationReason : null
		};

		this.isApprovalLoading = true;

		this.subscription.add(
			this.companyService.postAcceptRejectCompanyApproval(payload, this.companyId,).pipe(
				tap((response) => {
					if (response.Success) {
						this.toastr.success(response.Data.message || response.Message);
						this.getCompanyById();
						if (action === 'approve') {
							this.closeApproveModal();
						} else if (action === 'reject') {
							this.closeRejectModal();
						} else if (action === 'need_clarification') {
							this.closeClarificationModal();
						}
					} else {
						this.toastr.error(response.Message);
					}
				})
			).subscribe({
				error: (error) => {
					this.toastr.error(error?.error?.Message || 'Something went wrong');
				},
				complete: () => {
					this.isApprovalLoading = false;
				}
			})
		);
	}


	openApproveModal(company: any): void {
		this.selectedCompany = company;
		this.showApproveModal = true;
	}


	closeApproveModal(): void {
		this.showApproveModal = false;
	}


	openRejectModal(company: any): void {
		this.selectedCompany = company;
		this.showRejectModal = true;
	}


	closeRejectModal(): void {
		this.showRejectModal = false;
		this.rejectionReason = '';
	}


	openClarificationModal(company: any): void {
		console.log('openClarificationModal triggered for company details:', company);
		this.selectedCompany = company;
		this.showClarificationModal = true;
	}


	closeClarificationModal(): void {
		console.log('closeClarificationModal triggered');
		this.showClarificationModal = false;
		this.clarificationReason = '';
	}


	confirmApproval(): void {
		this.postAcceptRejectCompanyApproval('approve');
	}


	confirmRejection(): void {

		if (!this.rejectionReason.trim() || this.rejectionReason.trim().length < 5) {
			this.toastr.error('Please enter a rejection reason (minimum 5 characters)');
			return;
		}
		this.postAcceptRejectCompanyApproval('reject');
	}


	confirmClarification(): void {
		console.log('confirmClarification triggered. Reason:', this.clarificationReason);
		if (!this.clarificationReason.trim() || this.clarificationReason.trim().length < 5) {
			this.toastr.error('Please enter clarification details (minimum 5 characters)');
			return;
		}
		this.postAcceptRejectCompanyApproval('need_clarification');
	}


	onEditDetails(type: 'company' | 'contact'): void {
		if (type === 'company') {
			this.showCompanyEditModal = true;
		}
		if (type === 'contact') {
			this.showContactEditModal = true;
		}
	}


	editSection(section: 'company' | 'contact'): void {
		this.originalCompanyDetails = JSON.parse(JSON.stringify(this.companyDetails));
		if (section === 'company') {
			this.isEditingCompany = true;
		}
		if (section === 'contact') {
			this.isEditingContact = true;
		}
	}


	cancelEdit(section: 'company' | 'contact'): void {
		this.companyDetails = JSON.parse(JSON.stringify(this.originalCompanyDetails));
		if (section === 'company') {
			this.isEditingCompany = false;
		}
		if (section === 'contact') {
			this.isEditingContact = false;
		}
	}


	saveSection(section: 'company' | 'contact'): void {

		const payload = {
			company_name: this.companyDetails.company_name,
			industry: this.companyDetails.industry,
			company_url: this.companyDetails.company_url,
			linkedin_profile: this.companyDetails.linkedin_profile,
			about_company: this.companyDetails.about_company,
			office_locations: this.normalizeLocations(this.companyDetails.office_locations),
			poc_name: this.companyDetails.contact.poc_name,
			poc_email: this.companyDetails.contact.poc_email,
			poc_phone: this.companyDetails.contact.poc_phone,
			hr_name: this.companyDetails.contact.hr_name,
			hr_email: this.companyDetails.contact.hr_email,
			hr_phone: this.companyDetails.contact.hr_phone,
			official_email: this.companyDetails.contact.official_email
		};

		this.isSavingCompany = true;

		this.subscription.add(
			this.companyService.updateCompanyDetails(payload, this.companyId).pipe(
				finalize(() => {
					this.isSavingCompany = false;
				})
			).subscribe({
				next: (response: any) => {
					this.toastr.success(response?.message || 'Company updated successfully');
					if (section === 'company') {
						this.isEditingCompany = false;
					}
					if (section === 'contact') {
						this.isEditingContact = false;
					}
					this.getCompanyById();
				},
				error: (error: any) => {
					this.toastr.error(error?.error?.message || 'Something went wrong');
				}
			})
		);
	}


	private normalizeLocations(locations: any): string[] {
		if (!locations) return [];

		if (Array.isArray(locations)) {
			return locations.filter(x => typeof x === 'string' && x.trim().length > 0);
		}

		if (typeof locations === 'string') {
			return locations
				.split(',')
				.map(x => x.trim())
				.filter(x => x.length > 0);
		}

		return [];
	}


	onOfficeLocationsChange(value: string): void {
		if (!this.companyDetails) return;

		this.companyDetails.office_locations = value
			? value.split(',').map(x => x.trim()).filter(Boolean)
			: [];
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}